import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function NetworkLegend() {
  const [isExpanded, setIsExpanded] = useState(false);

  const metrics = [
    {
      name: "RSRP",
      description: "Signal strength of the LTE reference signal (dBm). Closer to 0 is stronger.",
      excellent: "≥ −90 dBm",
      good: "−100 to −90 dBm",
      fair: "−110 to −100 dBm",
      poor: "< −110 dBm"
    },
    {
      name: "RSRQ", 
      description: "Signal quality (dB). Closer to 0 is better; reflects interference & loading.",
      excellent: "≥ −7 dB",
      good: "−9 to −7 dB", 
      fair: "−12 to −9 dB",
      poor: "< −12 dB"
    },
    {
      name: "SNR",
      description: "Signal-to-noise (dB). Higher is better.",
      excellent: "≥ 20 dB",
      good: "10 to 20 dB",
      fair: "0 to 10 dB", 
      poor: "< 0 dB"
    },
    {
      name: "RSSI",
      description: "Broadband power (dBm). Often a proxy from CSQ on LTE.",
      excellent: "—",
      good: "≥ −80 dBm",
      fair: "−90 to −80 dBm",
      poor: "< −90 dBm"
    },
    {
      name: "CSQ",
      description: "0–31 index. Rough RSSI proxy. dBm ≈ −113 + 2×CSQ.",
      excellent: "—",
      good: "≥ 18",
      fair: "12 to 17",
      poor: "< 12"
    }
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <Button
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full p-0 h-auto font-medium text-left"
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            <span>What do these numbers mean?</span>
          </div>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Signal quality metrics help you understand network performance and choose the best connection.
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium">Metric</th>
                    <th className="text-left p-2 font-medium text-green-600">Excellent</th>
                    <th className="text-left p-2 font-medium text-blue-600">Good</th>
                    <th className="text-left p-2 font-medium text-yellow-600">Fair</th>
                    <th className="text-left p-2 font-medium text-red-600">Poor</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((metric) => (
                    <tr key={metric.name} className="border-b last:border-b-0">
                      <td className="p-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="flex items-center gap-1 cursor-help">
                              <span className="font-mono font-medium">{metric.name}</span>
                              <HelpCircle className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{metric.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="p-2 text-green-600 font-mono text-xs">{metric.excellent}</td>
                      <td className="p-2 text-blue-600 font-mono text-xs">{metric.good}</td>
                      <td className="p-2 text-yellow-600 font-mono text-xs">{metric.fair}</td>
                      <td className="p-2 text-red-600 font-mono text-xs">{metric.poor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Quality Score:</strong> Calculated from available metrics with fallback to RSSI/CSQ when LTE metrics aren't available.</p>
              <p><strong>Current Network:</strong> Highlighted with colored border. This is the network your device is currently using.</p>
              <p><strong>Recommended:</strong> Best available LTE network with successful attachment and highest quality score.</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}